/**
 * KeyComponent.js
 * Talkroute
 *
 * Created by Vel Genov on 5/10/19
 * Copyright (c) 2019 Talkroute. All right reserved.
 */

import React from "react";
import { Button } from "semantic-ui-react";
import { CSSTransition } from "react-transition-group";

import { CircularProgress, Fade } from "@material-ui/core";

import IconAnswer from "@resources/icons/dialer-answer.svg";
import IconDial from "@resources/icons/dialer-button-call.svg";
import IconKeypad from "@resources/icons/dialer-keypad.svg";
import IconKeypadActive from "@resources/icons/dialer-keypad-active.svg";
import IconEndCall from "@resources/icons/dialer-end-call.svg";
import IconMute from "@resources/icons/dialer-mute.svg";
import IconUnmute from "@resources/icons/dialer-mute-active.svg";
import IconHold from "@resources/icons/dialer-hold.svg";
import IconUnhold from "@resources/icons/dialer-hold-active.svg";
import IconRedial from "@resources/icons/redial-button.svg";
import { FilePathHelper } from "../../helpers";

// Types of KeyComponent
export const Types = {
  answer: "answer",
  text: "text",
  call: "call",
  blank: "blank",
  keypad: "keypad",
  keypadActive: "keypadActive",
  end: "end",
  mute: "mute",
  unmute: "unmute",
  hold: "hold",
  unhold: "unhold",
  redial: "redial",
};

// States of Button
const BUTTON_STATES = {
  NORMAL: "normal",
  HOVER: "hover",
  DOWN: "down",
};

const normalBorderColor = "var(--color-dark-grey)";
const normalTextColor = "var(--color-charcoal)";
const normalBackColor = "var(--color-white)";
const hoverTextBorderColor = "var(--color-charcoal)";
const hoverBackColor = "var(--color-white)";
const downTextBorderColor = "var(--color-charcoal)";
const downBackColor = "var(--color-light-grey)";

interface IKeyComponentProps {
  id?: string;
  keyTitle: string;
  keySubtitle: string;
  type: string;
  isPressed: boolean;
  isEnabled: boolean;
  isSoundEnabled: boolean;
  isVisible: boolean;
  audioPlayer?: HTMLAudioElement;
  clickActionCallback?: () => any;
}

/**
 * A component that represents a key on the phone keypad
 *
 * @param keyTitle the key title
 * @param keySubtitle the key subtitle
 * @param type the type of a kew this is
 * @param clickActionCallback a callback notified when this key is clicked
 */
class KeyComponent extends React.Component<IKeyComponentProps, any> {

  static defaultProps = {
    keyTitle: "",
    keySubtitle: "",
    type: Types.text,
    isPressed: false,
    isEnabled: true,
    isSoundEnabled: false,
    isVisible: true,
  };

  /**
   * Called when the button inside of this KeyComponent is clicked
   * Fires the clickActionCallback
   */
  onButtonClick = () => {
    const { clickActionCallback } = this.props;

    if (clickActionCallback) {
      clickActionCallback();
    }
  };

  /**
   * Displays the Key Title of button
   *
   * @param keyTitle the title of Button
   * @param textColor the color of text in Button
   */
  displayKeyTitle = (keyTitle, textColor) => {
    if (keyTitle === "*") {
      return (
        <div
          className="text-large"
          style={{
            fontSize: "50px",
            paddingTop: "15px",
            color: `${textColor}`,
          }}
        >
          {keyTitle}
        </div>
      );
    }

    return (
      <div
        className="text-large"
        style={{
          fontSize: "35px",
          marginTop: "-10px",
          color: `${textColor}`,
        }}
      >
        {keyTitle}
      </div>
    );
  };

  /**
   * Display a key that contains text
   */
  displayTextKey = () => {
    const {
      keyTitle,
      keySubtitle,
      isPressed,
      isSoundEnabled,
      isVisible,
    } = this.props;
    const { btnState } = this.state;

    let btnBorderColor;
    let btnTextColor;
    let btnBackColor;
    if (isPressed) {
      btnBorderColor = downTextBorderColor;
      btnTextColor = downTextBorderColor;
      btnBackColor = downBackColor;
    } else {
      switch (btnState) {
        case BUTTON_STATES.NORMAL:
          btnBorderColor = normalBorderColor;
          btnTextColor = normalTextColor;
          btnBackColor = normalBackColor;
          break;
        case BUTTON_STATES.HOVER:
          btnBorderColor = hoverTextBorderColor;
          btnTextColor = hoverTextBorderColor;
          btnBackColor = hoverBackColor;
          break;
        case BUTTON_STATES.DOWN:
          btnBorderColor = downTextBorderColor;
          btnTextColor = downTextBorderColor;
          btnBackColor = downBackColor;
          break;
        default:
          btnBorderColor = normalBorderColor;
          btnTextColor = normalTextColor;
          btnBackColor = normalBackColor;
          break;
      }
    }

    return (
      <CSSTransition
        key={keyTitle}
        in={isVisible}
        appear
        unmountOnExit
        mountOnEnter
        timeout={300}
        classNames="fade"
      >
        <Button
          circular
          className="key_content"
          onClick={() => this.onButtonClick()}
          style={{
            border: `1px solid ${btnBorderColor}`,
            backgroundColor: `${btnBackColor}`,
            position: "relative",
          }}
          onMouseEnter={() => this.setState({ btnState: BUTTON_STATES.HOVER })}
          onMouseLeave={() => this.setState({ btnState: BUTTON_STATES.NORMAL })}
          onMouseDown={() => {
            if (isSoundEnabled && this.props.audioPlayer) this.props.audioPlayer.play();
            this.setState({ btnState: BUTTON_STATES.DOWN });
          }}
          onMouseUp={() => this.setState({ btnState: BUTTON_STATES.HOVER })}
        >
          {this.displayKeyTitle(keyTitle, `${btnTextColor}`)}
          <div
            className="text-normal"
            style={{
              position: "absolute",
              top: "48px",
              left: "0",
              width: "100%",
              color: `${btnTextColor}`,
            }}
          >
            {keySubtitle}
          </div>
        </Button>
      </CSSTransition>
    );
  };

  /**
   * Display a call key
   */
  displayCallKey = () => {
    const { isEnabled } = this.props;

    return (
      <Fade key="fade-call" in timeout={500}>
        <Button
          circular
          disabled={!isEnabled}
          className="key_content"
          onClick={() => this.onButtonClick()}
          style={{ padding: "0" }}
        >
          <IconDial className="key_content"/>
        </Button>
      </Fade>
    );
  };

  /**
   * Display a redial key
   */
  displayRedialKey = () => {
    const { isEnabled } = this.props;

    return (
      <Fade in key="fade-redial" timeout={500}>
        <Button
          circular
          disabled={!isEnabled}
          className="key_content"
          onClick={() => this.onButtonClick()}
          style={{ padding: "0" }}
        >
          <IconRedial className="key_content"/>
        </Button>
      </Fade>
    );
  };

  /**
   * Display a keypad key
   */
  displayKeypadKey = () => {
    const { isEnabled } = this.props;

    if (!isEnabled) {
      return (
        <Button
          disabled
          circular
          className="key_content"
          onClick={() => this.onButtonClick()}
          style={{ padding: "0", opacity: "0.35" }}
        >
          <IconKeypad className="key_content" />
        </Button>
      );
    }

    return (
      <Button
        circular
        className="key_content"
        onClick={() => this.onButtonClick()}
        style={{ padding: "0" }}
      >
        <IconKeypad className="key_content" />
      </Button>
    );
  };

  /**
   * Display a keypadActive key
   */
  displayKeypadActiveKey = () => (
    <Button
      circular
      className="key_content"
      onClick={() => this.onButtonClick()}
      style={{ padding: "0" }}
    >
      <IconKeypadActive className="key_content" />
    </Button>
  );

  /**
   * Display an answer key
   */
  displayAnswerKey = () => (
    <Button
      circular
      className="key_content"
      onClick={() => this.onButtonClick()}
      style={{ padding: "0" }}
    >
      {/* <img src={answerSvg} className="key_content" alt="" /> */}
      <IconAnswer className="key_content" />
    </Button>
  );

  /**
   * Display a end key
   */
  displayEndKey = () => (
    <Button
      circular
      className="key_content"
      onClick={() => this.onButtonClick()}
      style={{ padding: "0" }}
    >
      <IconEndCall className="key_content" />
    </Button>
  );

  /**
   * Display a mute key
   */
  displayMuteKey = () => (
    <Button
      circular
      className="key_content"
      onClick={() => this.onButtonClick()}
      style={{ padding: "0" }}
    >
      <IconMute className="key_content" />
    </Button>
  );

  /**
   * Display an unmute key
   */
  displayUnmuteKey = () => (
    <Button
      circular
      className="key_content"
      onClick={() => this.onButtonClick()}
      style={{ padding: "0" }}
    >
      <IconUnmute className="key_content" />
    </Button>
  );

  /**
   * Display a hold key
   */
  displayHoldKey = () => (
    <Button
      circular
      className="key_content"
      onClick={() => this.onButtonClick()}
      style={{ padding: "0" }}
    >
      <IconHold className="key_content" />
    </Button>
  );

  /**
   * Display an unhold key
   */
  displayUnholdKey = () => (
    <Button
      circular
      className="key_content"
      onClick={() => this.onButtonClick()}
      style={{ padding: "0" }}
    >
      <IconUnhold className="key_content" />
    </Button>
  );

  /**
   * Determine the type of a key this is and display the appropriated content
   */
  displayKeyBody = () => {
    const { type } = this.props;

    switch (type) {
      case Types.answer:
        return this.displayAnswerKey();

      case Types.text:
        return this.displayTextKey();

      case Types.call:
        return this.displayCallKey();

      case Types.redial:
        return this.displayRedialKey();

      case Types.keypad:
        return this.displayKeypadKey();

      case Types.keypadActive:
        return this.displayKeypadActiveKey();

      case Types.end:
        return this.displayEndKey();

      case Types.mute:
        return this.displayMuteKey();

      case Types.unmute:
        return this.displayUnmuteKey();

      case Types.hold:
        return this.displayHoldKey();

      case Types.unhold:
        return this.displayUnholdKey();

      case Types.blank:
        return <div />;

      default:
        return <div />;
    }
  };

  constructor(props) {
    super(props);
    this.state = {
      btnState: BUTTON_STATES.NORMAL,
    };
  }

  componentDidUpdate(prevProps) {
    const { isPressed: prevIsPressed } = prevProps;
    const { isPressed, isSoundEnabled, audioPlayer } = this.props;

    if (audioPlayer && !audioPlayer.src && prevIsPressed !== isPressed && isPressed && isSoundEnabled) {
      audioPlayer.play();
    }
  }

  componentWillUnmount() {
    if (this.props.audioPlayer)
      this.props.audioPlayer.pause();
  }

  render() {
    const { isEnabled } = this.props;
    return (
      <div
        style={{
          display: "flex",
          position: "relative",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <div
          style={{
            opacity: isEnabled ? "1" : "0.1",
            pointerEvents: isEnabled ? "auto" : "none",
          }}
          id={this.props.id}
          className="key"
        >
          {this.displayKeyBody()}
        </div>
        {!isEnabled && (
          <CircularProgress
            style={{ position: "absolute", top: "23px", opacity: "0.8" }}
          />
        )}
      </div>
    );
  }
}

export default KeyComponent;
